import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import styles from '../../styles/modules/admin/AdminDashboard.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface DashboardStats {
  totalUsers: number;
  activeStaff: number;
  activeEmployers: number;
  activeJobOffers: number;
  activeAvailability: number;
  completedJobs: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeStaff: 0,
    activeEmployers: 0,
    activeJobOffers: 0,
    activeAvailability: 0,
    completedJobs: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('api/admin/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigationItems = [
    {
      key: 'users',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>,
      title: t('admin.navigation.users'),
      description: t('admin.navigation.usersDesc'),
      path: '/admin/users',
      disabled: false,
    },
    {
      key: 'employerTokens',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="8" cy="8" r="6"/>
        <path d="M8 5v6"/>
        <path d="M6 7.5h4"/>
        <circle cx="16" cy="16" r="6"/>
        <path d="M16 13v6"/>
        <path d="M14 15.5h4"/>
      </svg>,
      title: t('admin.navigation.employerTokens', { defaultValue: 'Employer Tokens' }),
      description: t('admin.navigation.employerTokensDesc', { defaultValue: 'List employers, view balances, and gift JobPost or QuickSearch tokens.' }),
      path: '/admin/tokens',
      disabled: false,
      featured: true,
    },
    {
      key: 'jobOffers',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>,
      title: t('admin.navigation.jobOffers'),
      description: t('admin.navigation.jobOffersDesc'),
      path: '/admin/job-offers',
      disabled: false,
    },
    {
      key: 'availability',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>,
      title: t('admin.navigation.availability'),
      description: t('admin.navigation.availabilityDesc'),
      path: '/admin/availability',
      disabled: false,
    },
    {
      key: 'notifications',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>,
      title: 'Notifications',
      description: 'Manage system notifications',
      path: '/admin/notifications',
      disabled: false,
    },
    {
      key: 'packages',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>,
      title: t('admin.navigation.packages', { defaultValue: 'Packages' }),
      description: t('admin.navigation.packagesDesc', { defaultValue: 'View and edit available subscription packages, prices, and visibility.' }),
      path: '/admin/packages',
      disabled: false,
    },
    {
      key: 'reports',
      icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="20" x2="12" y2="10"/>
        <line x1="18" y1="20" x2="18" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="16"/>
      </svg>,
      title: t('admin.navigation.reports'),
      description: t('admin.navigation.reportsDesc'),
      path: '/admin/reports',
      disabled: false,
    },
  ];

  if (loading) {
    return (
      <div className={styles.adminDashboard}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <header className={headerStyles.headerDashboard}>
        <h1 className={headerStyles.titleLarge}>{t('admin.title')}</h1>
        <button className={styles.logoutButton} onClick={() => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/');
        }} title={t('admin.logout')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </header>

      <div className={styles.adminContent}>
        
        <section className={styles.statsSection}>
          <h2>{t('admin.overview')}</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.totalUsers}</h3>
                <p>{t('admin.stats.totalUsers')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.activeStaff}</h3>
                <p>{t('admin.stats.activeStaff')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.activeEmployers}</h3>
                <p>{t('admin.stats.activeEmployers')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.activeJobOffers}</h3>
                <p>{t('admin.stats.activeJobOffers')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.activeAvailability}</h3>
                <p>{t('admin.stats.activeAvailability')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{stats.completedJobs}</h3>
                <p>{t('admin.stats.completedJobs')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.navigationSection}>
          <h2>{t('admin.management')}</h2>
          <div className={styles.navigationGrid}>
            {navigationItems.map((item) => (
              <div 
                key={item.key}
                className={`${styles.navigationCard} ${item.disabled ? styles.disabled : ''} ${item.featured ? styles.featuredCard : ''} ${item.disabled ? styles.navigationCardDisabled : ''}`}
                onClick={() => !item.disabled && navigate(item.path)}
              >
                <div className={styles.navIcon}>{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
